/*
 * <stat-counter> — số liệu PDP đếm động khi cuộn vào tầm nhìn.
 * Đọc data-stat-value (số đích), đếm 0→đích ~1.2s, format vi-VN vào .pdp-stats__count.
 * Hậu tố (.pdp-stats__suffix) render riêng trong markup nên KHÔNG đụng tới.
 * Custom element nên an toàn với section re-render / nhiều instance.
 */
class StatCounter extends HTMLElement {
  connectedCallback() {
    this.countEl = this.querySelector('.pdp-stats__count');
    if (!this.countEl || this.done) return;
    this.target = parseFloat(this.getAttribute('data-stat-value')) || 0;

    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.paint(this.target);
      this.done = true;
      return;
    }
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.animate();
        this.observer.disconnect();
      }
    }, { threshold: 0.05 }); // item chỉ ló một phần ở mép (peek) cũng phải đếm — 0.4 làm item peek đứng ở "0"
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  animate() {
    if (this.done) return;
    this.done = true;
    const duration = 1200;
    const start = performance.now();
    const step = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      this.paint(this.target * eased);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  paint(n) {
    this.countEl.textContent = Math.round(n).toLocaleString('vi-VN');
  }
}

if (!customElements.get('stat-counter')) {
  customElements.define('stat-counter', StatCounter);
}

/*
 * <stat-marquee> — cuộn ngang dải số liệu bằng 2 nút mũi tên (clone Coterie "Our diaper offers up to").
 * Mỗi lần bấm cuộn đúng 1 item (kèm gap); tự vô hiệu hoá nút ở hai đầu; đồng bộ khi resize.
 * Trên điện thoại nút ẩn (CSS) → vuốt chạm; JS vẫn an toàn nếu không có nút.
 */
class StatMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-stat-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-stat-dir="prev"]');
    this.next = this.querySelector('[data-stat-dir="next"]');
    this.onPrev = () => this.scrollByStep(-1);
    this.onNext = () => this.scrollByStep(1);
    this.onScroll = () => this.updateArrows();
    if (this.prev) this.prev.addEventListener('click', this.onPrev);
    if (this.next) this.next.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(() => this.updateArrows());
      this.ro.observe(this.track);
    }
    this.updateArrows();
  }

  disconnectedCallback() {
    if (this.prev) this.prev.removeEventListener('click', this.onPrev);
    if (this.next) this.next.removeEventListener('click', this.onNext);
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    if (this.ro) this.ro.disconnect();
  }

  step() {
    const item = this.track.querySelector('.pdp-stats__item');
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return item ? item.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
  }

  scrollByStep(dir) {
    this.track.scrollBy({ left: dir * this.step(), behavior: 'smooth' });
  }

  updateArrows() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const x = this.track.scrollLeft;
    if (this.prev) this.prev.disabled = x <= 2;
    if (this.next) this.next.disabled = x >= max - 2;
    // Fade mép chỉ khi còn nội dung khuất phía đó (CSS mask đọc 2 class này).
    this.classList.toggle('pdp-stats--scrolled', x > 2);
    this.classList.toggle('pdp-stats--at-end', x >= max - 2);
  }
}

if (!customElements.get('stat-marquee')) {
  customElements.define('stat-marquee', StatMarquee);
}
